<template>
  <div>
    <v-row
      class="mb-4"
      dense
    >
      <v-col>
        <div class="bc-h6">
          {{ $bct('Licensing & inspection history') }}
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col>
        <div class="c-black fw-600 bc-body-sm">
          {{ $bct('Certificates of compliance') }}
        </div>
      </v-col>
    </v-row>
    <v-row
      class="mb-3"
      dense
    >
      <v-col
        v-for="(cert, index) in license.certifications"
        :key="index"
        cols="12"
        lg="4"
        md="4"
      >
        <div class="b-1 b-radius-4 bc-light-gray bg-extra-light-gray c-black bc-caption px-3 py-2">
          <div class="fw-600 mb-1">
            {{ $bct(cert.type) }}
          </div>
          <div
            v-text="cert.number"
            class="mb-1"
          />
          <div
            v-text="cert.issue_date + ' - ' + cert.expiration_date"
            class="mb-1"
          />
        </div>
      </v-col>

      <v-col
        v-if="license.certifications.length == 0"
        cols="12"
      >
        <div class="c-light-black bc-body-sm">
          {{ $bct('No certificates of compliance.') }}
        </div>
      </v-col>
    </v-row>
    <v-row
      class="mb-1"
      dense
    >
      <v-col>
        <div class="c-black fw-600 bc-body-sm">
          {{ $bct('Inspections') }}
        </div>
      </v-col>
    </v-row>
    <v-row
      class="mb-3"
      dense
    >
      <v-col
        v-for="(inspection, index) in license.inspections"
        :key="index"
        cols="12"
        lg="4"
        md="4"
      >
        <div class="b-1 b-radius-4 bc-light-gray bg-extra-light-gray c-black bc-caption px-3 py-2">
          <div class="fw-600 mb-1">
            {{ $bct('Inspection') }}
          </div>
          <div
            v-text="inspection.date"
            class="mb-1"
          />
          <div
            v-text="inspection.number"
            class="mb-1"
          />
          <div
            v-text="`${inspection.findings.length} ${$bct('corrections')}`"
            class="mb-1"
          />
          <div
            @click="showInspection(inspection)"
            class="mb-1 c-secondary pointer"
          >
            <span class="bc-link-hover">{{ $bct('View inspection details') }}</span>
          </div>
        </div>
      </v-col>
      <v-col
        v-if="license.inspections.length == 0"
        cols="12"
      >
        <div class="c-light-black bc-body-sm">
          {{ $bct('No inspections.') }}
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col>
        <div class="c-black fw-600 bc-body-sm">
          {{ $bct('Waivers of regulation') }}
        </div>
      </v-col>
    </v-row>
    <v-row
      class="mb-3"
      dense
    >
      <v-col
        v-for="(waiver, index) in license.waivers"
        :key="index"
        cols="12"
        lg="4"
        md="4"
      >
        <div class="b-1 b-radius-4 bc-light-gray bg-extra-light-gray c-black bc-caption px-3 py-2">
          <div
            v-text="`${$bct('Regulation')} # ${waiver.regulation_number}`"
            class="fw-600 mb-1"
          />
          <div class="mb-1">
            {{ $bct(waiver.regulation_description) }}
          </div>
          <div
            v-text="waiver.date"
            class="mb-1"
          />
          <div class="mb-1">
            {{ $bct(waiver.status) }}
          </div>
        </div>
      </v-col>
      <v-col
        v-if="license.waivers.length == 0"
        cols="12"
      >
        <div class="c-light-black bc-body-sm">
          {{ $bct('No waivers requested.') }}
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col>
        <div class="c-black fw-600 bc-body-sm">
          {{ $bct('Negative Sanctions') }}
        </div>
      </v-col>
    </v-row>
    <v-row
      class="mb-3"
      dense
    >
      <v-col
        v-for="(sanction, index) in license.sanctions"
        :key="index"
        cols="12"
        lg="4"
        md="4"
      >
        <div class="b-1 b-radius-4 bc-light-gray bg-extra-light-gray c-black bc-caption px-3 py-2">
          <div class="fw-600 mb-1">
            {{ $bct(sanction.type) }}
          </div>
          <div
            v-text="sanction.number"
            class="mb-1"
          />
          <div
            v-text="sanction.date"
            class="mb-1"
          />
          <div class="mb-1">
            {{ $bct(sanction.status) }}
          </div>
        </div>
      </v-col>
      <v-col
        v-if="license.sanctions.length == 0"
        cols="12"
      >
        <div class="c-light-black bc-body-sm">
          {{ $bct('No negative sanctions.') }}
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="ta-right">
        <a
          class="bc-caption bc-link-hover"
          href="https://www.compass.state.pa.us/compass.web/ProviderSearch/Home#/BasicSearch"
          target="_blank"
        >
          {{ $bct('Licensing data provided by Compass') }}
        </a>
      </v-col>
    </v-row>

    <v-dialog
      v-model="inspectionDialogIsVisible"
      max-width="770"
    >
      <v-card v-if="inspection">
        <v-card-text>
          <v-row>
            <v-col cols="11">
              <div class="c-primary bc-h6">
                {{ $bct('Inspection details') }}
              </div>
            </v-col>
            <v-col
              class="ta-right"
              cols="1"
            >
              <v-btn
                @click="inspectionDialogIsVisible = false"
                variant="text"
                icon
              >
                <v-icon icon="close" />
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <div class="fs-10 c-light-black">
                {{ $bct('INSPECTION ID') }}
              </div>
              <div
                v-text="inspection.number"
                class="c-black bc-caption"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <div class="fs-10 c-light-black">
                {{ $bct('INSPECTION DATE') }}
              </div>
              <div
                v-text="inspection.date"
                class="c-black bc-caption"
              />
            </v-col>
          </v-row>
          <Finding
            v-for="(finding, index) in inspection.findings"
            :key="index"
            :finding="finding"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import Finding from '@/shared/components/search/finding.vue';

const $bct = inject('translator');

defineProps({
  license: {
    type: Object,
    default: null,
  },
});

const inspection = ref(null);
const inspectionDialogIsVisible = ref(false);

function showInspection(inspectionValue) {
  inspectionDialogIsVisible.value = true;
  inspection.value = inspectionValue;
}
</script>
